import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import { getRecentThesisSubmitted } from "../../services/thesisAssignedService";

import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VivaChatWidget from "./VivaChatWidget";
import { isSlotActive } from "../../hooks/ChatUtils";


const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
      onMouseDown={(e) => {
        // close when clicking backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: 480,
          background: "#fff",
          borderRadius: 8,
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const AssignExaminerList = () => {

  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadingRows, setUploadingRows] = useState({});

  const [file, setFile] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const [notifying, setNotifying] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Google Meet scheduling approval modal
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSessionKeys, setSelectedSessionKeys] = useState([]);
  const [creatingMeets, setCreatingMeets] = useState(false);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // form state for modal
  const [form, setForm] = useState({
    vivaId: null, // existing viva_schedule.id (if any)
    thesisStatusId: null,
    date: "",
    startTime: "",
    endTime: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getRecentThesisSubmitted();
      const data = res?.data || [];
      setRows(data);

    } catch (err) {
      console.error("fetch error", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Open modal and populate form from row
  const openModalForRow = (row) => {
    if (!row) return;
    const vivaId = row.vivaId ?? null;
    const date = row.date ?? row.vs_date ?? "";
    const startTime = row.startTime ?? row.start_time ?? row.vs_start_time ?? "";
    const endTime = row.endTime ?? row.end_time ?? row.vs_end_time ?? "";

    setForm({
      vivaId,
      thesisStatusId: row.id, // thesis_status.id
      date: date || "",
      startTime: startTime || "",
      endTime: endTime || "",
    });
    setEditingRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setTimeout(
      () =>
        setForm({
          vivaId: null,
          thesisStatusId: null,
          date: "",
          startTime: "",
          endTime: "",
        }),
      200
    );
  };

  const validateForm = () => {
  if (!form.date) {
    toast.error("Please select a viva date.");
    return false;
  }
  if (!form.startTime) {
    toast.error("Please select a start time.");
    return false;
  }
  if (!form.endTime) {
    toast.error("Please select an end time.");
    return false;
  }
  if (form.startTime >= form.endTime) {
    toast.error("Start time must be before end time.");
    return false;
  }
  return true;
};


  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      id: form.vivaId ?? null,
      thesisStatusId: form.thesisStatusId,
      date: form.date || null,
      startTime: form.startTime || null,
      endTime: form.endTime || null,
    };

   // console.log("Viva payload ->", payload);

    try {
      if (form.vivaId) {
        await axiosInstance.put("/updateVivaSchedule", payload);
        toast.success("Viva schedule updated");
      } else {
        await axiosInstance.post("/addVivaSchedule", payload);
        toast.success("Viva schedule created");
      }
      await fetchData();
      await axiosInstance.post("/viva/generate-slots");
      await fetchSlots();
      closeModal();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save viva schedule");
    }
  };

  // Safe valueGetters to avoid params.row undefined crash
  const safeGet = (params, ...keys) => {
    const row = params?.row;
    if (!row) return "";
    for (let k of keys) {
      if (row[k] != null) return row[k];
    }
    return "";
  };

  const shareViva = async (vivaId, isShared) => {
    try {
      await axiosInstance.put(`/shareVivaSchedule/${vivaId}`);

      setRows((prevRows) =>
        prevRows.map((row) =>
          row.vivaId === vivaId
            ? { ...row, share_to_student: !isShared }
            : row
        )
      );

      toast.success(
        isShared
          ? "Viva unshared successfully"
          : "Viva shared with student"
      );
    } catch (err) {
      toast.error("Failed to update viva share status");
      console.error(err);
    }
  };

  const handleNotifyExaminers = async () => {
    if (selectedSessionKeys.length === 0) {
      toast.error("Select at least one session first");
      return;
    }
    setNotifying(true);
    try {
      const res = await axiosInstance.post("/notifyExaminers", selectedSessionKeys);
      toast.success(res.data?.message || "Examiners notified");
      setSelectedSessionKeys([]);
    } catch (err) {
      toast.error("Failed to notify examiners");
      console.error(err);
    } finally {
      setNotifying(false);
    }
  };

  const handleSendReminders = async () => {
    if (selectedSessionKeys.length === 0) {
      toast.error("Select at least one session first");
      return;
    }
    setSendingReminders(true);
    try {
      const res = await axiosInstance.post("/sendVivaReminders", selectedSessionKeys);
      toast.success(res.data?.message || "Reminders sent");
      setSelectedSessionKeys([]);
    } catch (err) {
      toast.error("Failed to send reminders");
      console.error(err);
    } finally {
      setSendingReminders(false);
    }
  };

  const openSessionsModal = async () => {
    setSessionsModalOpen(true);
    setLoadingSessions(true);
    setSelectedSessionKeys([]);
    try {
      const res = await axiosInstance.get("/viva-sessions/preview");
      setSessions(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to load sessions");
      console.error(err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const toggleSessionSelected = (sessionKey) => {
    setSelectedSessionKeys((prev) =>
      prev.includes(sessionKey)
        ? prev.filter((k) => k !== sessionKey)
        : [...prev, sessionKey]
    );
  };

  const handleCreateSelectedMeets = async () => {
    if (selectedSessionKeys.length === 0) {
      toast.error("Select at least one session first");
      return;
    }
    setCreatingMeets(true);
    try {
      const res = await axiosInstance.post("/viva-sessions/create-meets", selectedSessionKeys);
      toast.success(res.data?.message || "Meetings created");
      setSelectedSessionKeys([]);
      // refresh so newly created links show up and can't be re-selected
      const refreshed = await axiosInstance.get("/viva-sessions/preview");
      setSessions(refreshed.data?.data || []);
    } catch (err) {
      toast.error("Failed to create meetings");
      console.error(err);
    } finally {
      setCreatingMeets(false);
    }
  };

  const uploadSingleFile = async ({
    rowId,
    fileType,
    thesisStatusId,
    announcementId,
    studentId,
    termType,
    reportType,
    clearState
  }) => {

    const file = selectedFiles[rowId];

    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    try {
      setUploadingRows(prev => ({
        ...prev,
        [rowId]: true
      }));

      const presignRes = await axiosInstance.post(
        "/files/presign-upload",
        {
          thesisStatusId,
          studentId,
          announcementId,
          termType,
          fileType,
          reportType,
          filename: file.name
        }
      );

      const { uploadUrl, objectKey } = presignRes.data;

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream"
        }
      });

      if (!uploadRes.ok) {
        throw new Error("Object storage upload failed");
      }

      await axiosInstance.post("/files/save-ai-summary", {
        thesisStatusId,
        studentId,
        fileType,
        objectKey,
        originalFilename: file.name
      });

      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId
            ? { ...row, ai_summary_exists: true }
            : row
        )
      );

      toast.success("AI Summary uploaded successfully");

      if(clearState){
        clearState(null);
      }

      setSelectedFiles(prev => ({
        ...prev,
        [rowId]: null
      }));

    } catch (err) {
      console.error(err);
      toast.error(err.message || "Upload failed");
    } finally {
      setUploadingRows(prev => ({
        ...prev,
        [rowId]: false
      }));
    }
  };

  const columns = [
    {
      field: "thesis_title",
      headerName: "Title",
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        const isExpanded = !!expandedRows[params.row.id];
        const title = params.row.thesis_title || "";
        return (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 4, width: "100%" }}>
            <span
              style={
                isExpanded
                  ? { whiteSpace: "normal", wordBreak: "break-word", lineHeight: "16px" }
                  : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
              }
              title={title}
            >
              {title}
            </span>
            {title.length > 24 && (
              <IconButton size="small" onClick={() => toggleExpand(params.row.id)}>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </div>
        );
      },
    },
    { field: "student_name", headerName: "Student Name", width: 150 },
    { field: "enrollment_number", headerName: "Enrollment No", width: 100 },
    { field: "degree_name", headerName: "Degree", width: 90 },
    { field: "program_name", headerName: "Program", width: 150 },
    { field: "report_type", headerName: "Report Type", width: 100 },
    { field: "term_type", headerName: "Submission Type", width: 100 },
    { field: "date_submitted", headerName: "Date Submitted", width: 140 },
    {
      field: "vivaSchedule",
      headerName: "Viva Schedule",
      width: 150,
      renderCell: (params) => {
      const { date, startTime, endTime } = params.row || {};

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", padding: "4px 0" }}>

          <div style={{ lineHeight: "16px" }}>
            {date && startTime && endTime ? (
              <>
                <div style={{ fontSize: 12 }}>{date}</div>
                <div style={{ fontSize: 11, color: "#666" }}>
                  {startTime} – {endTime}
                </div>
              </>
            ) : (
              <div style={{ color: "#999", fontSize: 12 }}>Not Scheduled</div>
            )}
          </div>

          <div>
            <Tooltip title="Edit Viva">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!params || !params.row) return;
                  openModalForRow(params.row);
                }}
              >
                <EditIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>

            {date && startTime && endTime && (
              <Tooltip title={params.row.share_to_student ? "Unshare" : "Share"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    shareViva(params.row.vivaId, params.row.share_to_student);
                  }}
                >
                  {params.row.share_to_student ? (
                    <LinkOffIcon fontSize="small" />
                  ) : (
                    <LinkIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </div>

        </div>
      );
    }
    },

    {
      field: "action",
      headerName: "Action",
      width: 160,
      renderCell: (params) => {
        // guard
        if (!params || !params.row) return null;
        const { id, reviewer_id1, reviewer_id2, examiner_id1, examiner_id2, examiner1_name, examiner2_name, reviewer1_name, reviewer2_name } = params.row;
        const date =
            params.row.date ?? params.row.vs_date;

          const startTime =
            params.row.startTime ?? params.row.start_time ?? params.row.vs_start_time;

          const endTime =
            params.row.endTime ?? params.row.end_time ?? params.row.vs_end_time;

        return (
          <div className="cellAction" style={{ display: "flex", flexDirection: "column", gap: 2, padding: "4px 0" }}>
            <div className="viewButton">
              <Link to={`view-assigned/${id}`}>View/Edit</Link>
            </div>

            {(examiner_id1 || examiner_id2 || reviewer_id1 || reviewer_id2) ? (
              <div style={{ lineHeight: "16px", fontSize: 12 }}>
              <div className="editButton"> {examiner1_name} – {examiner2_name} </div>
              <div className="editButton"> {reviewer1_name} – {reviewer2_name}</div>
              </div>

              /* <div className="editButton">Examiners Assigned</div> */
            ) : (
              <div className="editButton">
                <Link to={`assignexaminer/${id}`}>Assign Examiners</Link>
              </div>
              
            )}

          </div>
        );
      },
    },

    {
      field: "aiSummary",
      headerName: "AI Summary",
      width: 190,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <div className="cellAction" style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0", width: "100%" }}>
          <input
            type="file"
            style={{ width: "100%", fontSize: 11 }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setSelectedFiles(prev => ({
                ...prev,
                [params.row.id]: e.target.files[0]
              }))
            }
          />

          <div style={{ display: "flex", gap: 4 }}>
          {params.row.ai_summary_exists &&
            <button
            onClick={async (e) => {
              e.stopPropagation();

              try {
                const res = await axiosInstance.get(
                  "/files/presign-download-aiSummary",
                  {
                    params: {
                      thesisStatusId: params.row.id,
                      fileType: "Ai_Summary"
                    }
                  }
                );

                const responseData = res.data?.data;

                if (!responseData?.downloadUrl) return;

                window.open(responseData.downloadUrl, "_blank");

              } catch (err) {
                if (err.response?.status === 404) {
                  toast.error("AI Summary not found");
                } else {
                  console.error(err);
                  toast.error("Failed to fetch file");
                }
              }
            }}
            style={{
              padding: "4px 8px",
              fontSize: 11,
              background: "#4caf50",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer"
            }}
          >
            View
          </button>
          }
          <button
            title="Upload AI Summary"
            onClick={(e) => {
              e.stopPropagation();
              uploadSingleFile({
                rowId: params.row.id,
                fileType: "Ai_Summary",
                thesisStatusId: params.row.id,
                announcementId: params.row.announcement_id,
                studentId: params.row.student_id,
                termType: params.row.term_type,
                reportType: params.row.report_type,
                clearState: setFile
              });
            }}
            disabled={uploadingRows[params.row.id]}
            style={{
              padding: "4px 8px",
              fontSize: 11,
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: uploadingRows[params.row.id] ? "not-allowed" : "pointer"
            }}
          >
            {uploadingRows[params.row.id] ? "Uploading..." : "Upload"}
          </button>
          </div>

          </div>
        );
      },
    },
  ];

  const [slots, setSlots] = useState([]);
  const [enrichedSlots, setEnrichedSlots] = useState([]);

  useEffect(() => {
    const enriched = slots.map(slot => ({
      ...slot,
      students: rows.filter(r => slot.vivaIds?.includes(r.vivaId))
    }));

    setEnrichedSlots(enriched);
  }, [slots, rows]);

  useEffect(() => {
    fetchSlots();
  }, []);


  const fetchSlots = async () => {
    try {
      const res = await axiosInstance.get("/viva/slots");

      const slotData = res.data?.data || [];
      setSlots(slotData);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="datatable">
        <div
          className="datatableTitle"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <span>Recent Submissions List</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={openSessionsModal}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#2e7d32",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Manage Viva Sessions
            </button>
          </div>
        </div>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          pageSize={10}
          autoHeight

        />
      </div>

      <Modal open={modalOpen} onClose={closeModal}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Student</label>
            <div style={{ fontWeight: 600 }}>{editingRow?.student_name ?? "-"}</div>
            <div style={{ color: "#666", fontSize: 12 }}>{editingRow?.enrollment_number ?? ""}</div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Viva Date</label>
            <input
            type="date"
            value={form.date || ""}
            onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: 4,
              border: form.date ? "1px solid #ccc" : "1px solid red",
            }}
          />

          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Start Time</label>
              <input
              type="time"
              value={form.startTime || ""}
              onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: 4,
                border: form.startTime ? "1px solid #ccc" : "1px solid red",
              }}
            />

            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>End Time</label>
              <input
              type="time"
              value={form.endTime || ""}
              onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: 4,
                border: form.endTime ? "1px solid #ccc" : "1px solid red",
              }}
            />
            </div>
          </div>

          <div style={{ 
            marginTop: 10, 
            fontSize: 12, 
            color: "#555", 
            background: "#f8f8f8", 
            padding: "10px 12px", 
            borderRadius: 6,
            lineHeight: "18px"
          }}>
            <strong>Tip:</strong>  
            Time fields may show a default value (such as <em>12:30 PM</em>).  
            Please enter the full time manually — including <strong>hours</strong>, 
            <strong>minutes</strong>, and <strong>AM/PM</strong>.  
            If only part of the field is edited, the system may treat the time as not entered.
          </div>


          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={handleSave}
              disabled={!form.date || !form.startTime || !form.endTime}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                cursor: (!form.date || !form.startTime || !form.endTime) ? "not-allowed" : "pointer",
              }}
            >
              Save
            </button>

            <button
              onClick={closeModal}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                background: "#eee",
                color: "#222",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>


        </div>
      </Modal>

      <Modal open={sessionsModalOpen} onClose={() => setSessionsModalOpen(false)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "70vh" }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Manage Viva Sessions</div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Select the session(s) you want to act on, then choose an action below: notify
            examiners/students, send a reminder, or create a Google Meet. Sessions that already have
            a meeting show their link.
          </div>

          <div style={{ overflowY: "auto", maxHeight: "45vh", border: "1px solid #eee", borderRadius: 6 }}>
            {loadingSessions ? (
              <div style={{ padding: 16, textAlign: "center", color: "#666" }}>Loading sessions...</div>
            ) : sessions.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "#666" }}>No upcoming sessions found.</div>
            ) : (
              sessions.map((session) => {
                const panel = [
                  session.examiner1Name, session.examiner2Name,
                  session.reviewer1Name, session.reviewer2Name,
                ].filter(Boolean).join(" / ");

                const alreadyCreated = Boolean(session.meetLink);

                return (
                  <label
                    key={session.sessionKey}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "10px 12px",
                      borderBottom: "1px solid #f0f0f0",
                      alignItems: "flex-start",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSessionKeys.includes(session.sessionKey)}
                      onChange={() => toggleSessionSelected(session.sessionKey)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {session.date} · {session.startTime} - {session.endTime}
                      </div>
                      <div style={{ fontSize: 12, color: "#444" }}>{panel}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>
                        {(session.students || []).map((s) => s.studentName).join(", ")}
                      </div>
                      {alreadyCreated && (
                        <div style={{ fontSize: 11, color: "#2e7d32", marginTop: 4 }}>
                          Meeting already created: <a href={session.meetLink} target="_blank" rel="noopener noreferrer">{session.meetLink}</a>
                        </div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setSessionsModalOpen(false)}>Close</button>
            <button
              type="button"
              onClick={handleNotifyExaminers}
              disabled={notifying || selectedSessionKeys.length === 0}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#1976d2",
                color: "#fff",
                fontWeight: 600,
                cursor: notifying || selectedSessionKeys.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {notifying ? "Notifying..." : `Notify Examiners (${selectedSessionKeys.length})`}
            </button>
            <button
              type="button"
              onClick={handleSendReminders}
              disabled={sendingReminders || selectedSessionKeys.length === 0}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#f57c00",
                color: "#fff",
                fontWeight: 600,
                cursor: sendingReminders || selectedSessionKeys.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {sendingReminders ? "Sending..." : `Send Reminders (${selectedSessionKeys.length})`}
            </button>
            <button
              type="button"
              onClick={handleCreateSelectedMeets}
              disabled={creatingMeets || selectedSessionKeys.length === 0}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#2e7d32",
                color: "#fff",
                fontWeight: 600,
                cursor: creatingMeets || selectedSessionKeys.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {creatingMeets ? "Creating..." : `Create Meets (${selectedSessionKeys.length})`}
            </button>
          </div>
        </div>
      </Modal>

      <div>
        <div>
        {enrichedSlots
          .filter(slot => isSlotActive(slot))
          .map((slot, index) => (
            <VivaChatWidget
              key={slot.id}
              slot={slot}
              index={index}
              role="ADMIN"
            />
        ))}
        </div>
      </div>
    </div>
  );
};

export default AssignExaminerList;
